import React from "react";
import type { ApolloClient } from "@apollo/client";
import { Col, Form, Row } from "react-bootstrap";
import { Formik, type FormikProps } from "formik";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { toast } from "react-toastify";

import Alert from "components/Alert";
import Button from "components/Button";

import { updateBillingEmail } from "actions/adminActions";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { adminConstants } from "actions/types";
import { ScrollToFieldError } from "utils/form";
import type { RootState } from "types/state";

import * as yup from "yup";
import { emailValidator } from "utils/fieldValidators";
const schema = yup.object({
  billingContactEmail: emailValidator("You must enter a valid email address")
    .label("Billing Contact Email")
    .required(),
});

interface FormValues {
  billingContactEmail: string;
}

interface State {
  setupFees: unknown[];
  subscriptions: unknown[];
  perUserFees: unknown[];
}

const errorSelector = createErrorSelector([
  adminConstants.UPDATE_BILLING_CONTACT,
]);
const loadingSelector = createLoadingSelector(
  adminConstants.UPDATE_BILLING_CONTACT
);

const mapStateToProps = (state: RootState) => {
  return {
    error: errorSelector(state),
    isSubmitting: loadingSelector(state),
  };
};

const mapDispatchToProps = {
  updateBillingEmail,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

/** Props supplied by the parent (this form is a child invoked with callbacks). */
interface OwnProps {
  onSuccess?: (data?: unknown) => void;
  onClose?: () => void;
  billingContactEmail: string;
  companyId?: string;
}

type Props = WithApolloClient<ConnectedProps<typeof connector> & OwnProps>;

class BillingForm extends React.PureComponent<Props, State> {
  constructor() {
    // @ts-expect-error preserve verbatim: master calls super() with no args
    super();

    this.state = {
      setupFees: [],
      subscriptions: [],
      perUserFees: [],
    };
  }

  _onSubmit = (vals: FormValues) => {
    const payload = {
      email: vals.billingContactEmail,
      companyId: this.props.companyId,
    };
    void this.props
      // companyId is optional on OwnProps (string | undefined) but the action
      // requires string | number; pass the exact runtime value through unchanged.
      .updateBillingEmail(
        this.props.client as ApolloClient<object>,
        payload as { email: string; companyId: string | number }
      )
      .then((result) => {
        if (result.success) {
          toast.success("Successfully updated billing contact email.");
          this.props.onSuccess!(result.data);
        }
      });
  };

  render() {
    return (
      <div className="billing-form">
        <Formik
          validateOnChange={false}
          validationSchema={schema}
          onSubmit={this._onSubmit}
          enableReinitialize={true}
          initialValues={{
            billingContactEmail: this.props.billingContactEmail,
          }}
        >
          {({
            handleSubmit,
            handleChange,
            values,
            touched,
            errors,
            handleBlur,
          }: FormikProps<FormValues>) => (
            <Form noValidate onSubmit={handleSubmit}>
              <ScrollToFieldError />

              <Row className="mb-3">
                <Form.Group as={Col} sm={6} controlId="formBasicUserEmail">
                  <Form.Label>Billing Contact Email</Form.Label>
                  <Form.Control
                    name="billingContactEmail"
                    placeholder="Billing Contact Email"
                    value={values.billingContactEmail}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    isInvalid={
                      touched.billingContactEmail &&
                      !!errors.billingContactEmail
                    }
                    isValid={
                      touched.billingContactEmail && !errors.billingContactEmail
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.billingContactEmail}
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>
              <Form.Group className="mb-3">
                {this.props.error && (
                  <Alert type="error" msg={this.props.error} />
                )}
                <div className="btn-row">
                  <Button
                    onClick={this.props.onClose}
                    btnLabel={"Cancel"}
                    color={"cancel"}
                    name="cancel"
                  />
                  <Button
                    btnLabel="Update Contact"
                    name="submit"
                    withArrow={true}
                    loading={this.props.isSubmitting}
                  />
                </div>
              </Form.Group>
            </Form>
          )}
        </Formik>
      </div>
    );
  }
}

export default connector(withApollo<Props>(BillingForm));
